import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardText,
  Col,
  Container,
  Form,
  Input,
  Row,
} from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';

import { getUsersListTableColumns } from '../../../data/formsAndTable/tables/dataTables/DataSource';

import { use, useEffect, useMemo, useRef, useState } from 'react';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import { AddUserFormInputs, AddUserInput, CalendarItem, UserRole } from '../../../types/Forms';
import ValidationForm from '../../formsAndTable/forms/formControls/formValidation/validationForm';
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useEditUserMutation,
  useGetAllCoursesQuery,
  useGetAllUsersQuery,
} from '../../../reduxToolkit/reducers/usersSlice';
import { UserData } from '../../../types/Tables';
import { useAppSelector } from '../../../reduxToolkit/hooks';
import Confirmation from '../../../coreComponents/Confirmation';
import { useTranslation } from 'react-i18next';
import { enumToSelectOptions } from '../../../utils';
import AjaxSourcedData from '../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { ImportResult } from '../../../types/Users';
import { importUsers } from '../../../services/api';
import AddUserForm from './AddUserForm';
import CommonCardHeader from '../../../coreComponents/CommonCardHeader';
import { getUserAddInputs } from './inputs';
import { NoDataComponent } from '../../formsAndTable/forms/formControls/NoDataComponent';
import { read, utils } from 'xlsx';
import Swal from 'sweetalert2';
import { ICourse } from '../../../types/Courses';
import { baseLang } from '../../../lib/utils';

export const formDefault = {
  firstName: '',
  lastName: '',
  email: '',
  telegram: '@',
  password: '',
  role: UserRole.STUDENT,
  photo: undefined,
  dob: new Date().toISOString().split('T')[0],
  city: '',
  coursesOptions: [{ value: '', label: '' }],
};

const UsersListPageContainer = () => {
  const { role } = useAppSelector((state) => state.auth);
  const [defaultFormValues, setDefaultFormValues] =
    useState<Partial<AddUserFormInputs>>(formDefault);
  const [modalAction, setModalAction] = useState({
    id: 1,
    action: 'create',
  });
  const { data, error, isLoading, isFetching } = useGetAllUsersQuery({});
  const { data: allCourses } = useGetAllCoursesQuery({});
  const [createUser] = useCreateUserMutation();
  const [editUser] = useEditUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmation, setConfirmation] = useState<number | null>(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<UserData | null>(null);

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(0);

  const [openFileUpload, setOpenFileUpload] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResultModalOpen, setImportResultModalOpen] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const currentUser = useAppSelector((state) => state.auth);
  const { t, i18n } = useTranslation();
  const pageLang: 'en' | 'uk' | 'ru' = baseLang(i18n.language) as 'en' | 'uk' | 'ru';

  const addNewUserButtonHandler = () => {
    setModalAction({
      id: 1,
      action: 'create',
    });
    setDefaultFormValues({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      city: '',
      dob: new Date().toISOString().split('T')[0],
      role: UserRole.STUDENT,
      photo: undefined,
      coursesOptions: [{ value: '', label: '' }],
    });
    setModalIsOpen(true);
  };

  const coursesListOptions = useMemo(() => {
    if (!allCourses) return [];
    if (pageLang === 'en') {
      return allCourses.map((course: ICourse) => ({
        value: String(course.id),
        label: course.name,
      }));
    } else {
      return allCourses.map((course: ICourse) => ({
        value: String(course.id),
        label: course.translations.find((t) => t.lang === pageLang)?.name || course.name,
      }));
    }
  }, [allCourses, pageLang]);

  const newUserSubmitHandler = async (data: any): Promise<void> => {
    const formData = new FormData();
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('email', data.email);
    formData.append('telegram', data.telegram);
    formData.append('password', data.password || '');
    formData.append('city', data.city || '');
    formData.append('dob', data.dob);
    formData.append('role', data.role);
    formData.append('courseIds', JSON.stringify(data.coursesOptions));
    if (data.photo instanceof File) {
      formData.append('photo', data.photo);
    }
    try {
      const newStudent = await createUser(formData);
      setModalIsOpen(false);

      if (newStudent && 'error' in newStudent) {
        throw new Error('Creation error');
      }

      setTimeout(
        () =>
          Swal.fire({
            icon: 'success',
            title: t('users.usersList.userCreatedSuccessTitle'),
            text: t('users.usersList.userCreatedSuccessText'),
          }),
        1000,
      );
    } catch (error) {
      console.log('error', error);
      setTimeout(
        () =>
          Swal.fire({
            icon: 'error',
            title: t('users.usersList.userCreatedErrorTitle'),
            text: t('users.usersList.userCreatedErrorText'),
          }),
        1000,
      );
    }
  };
  const editUserSubmitHandler = async (data: any): Promise<void> => {
    const formData = new FormData();

    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('telegram', data.telegram);
    formData.append('email', data.email);
    formData.append('password', data.password || '');
    formData.append('city', data.city || '');
    formData.append('dob', data.dob);
    formData.append('role', data.role);
    formData.append('id', currentUserId.toString());
    formData.append('courseIds', JSON.stringify(data.coursesOptions));

    if (data.photo instanceof File) {
      formData.append('photo', data.photo);
    }
    try {
      const res = await editUser(formData).unwrap();
      console.log('editUser res', res);
      setTimeout(
        () =>
          Swal.fire({
            icon: 'success',
            title: t('users.usersList.userUpdatedSuccessTitle'),
            text: t('users.usersList.userUpdatedSuccessText'),
          }),
        1000,
      );
    } catch (error) {
      console.log('error', error);
      setTimeout(
        () =>
          Swal.fire({
            icon: 'error',
            title: t('users.usersList.userUpdatedErrorTitle'),
            text: t('users.usersList.userUpdatedErrorText'),
          }),
        1000,
      );
    } finally {
      setCurrentUserId(0);
      setModalIsOpen(false);
      setDefaultFormValues(formDefault);
      setModalAction({
        id: 1,
        action: 'create',
      });
    }
  };
  const AddStudentButtonProps = {
    id: '1',
    outline: true,
    color: 'primary',
    hoverColor: 'secondary',
  };

  const isUserRole = (value: any): value is UserRole => Object.values(UserRole).includes(value);
  const handleUserEdit = (rowData: UserData) => {
    setCurrentUserId(rowData.id);
    setModalAction({
      id: 2,
      action: 'edit',
    });
    const cOpts =
      rowData.studentCourses?.map((c) => ({
        value: c.courseId?.toString(),
        label: '',
      })) || [];
    const editableData = {
      firstName: rowData.firstName,
      lastName: rowData.lastName,
      telegram: rowData.telegram,
      email: rowData.email,
      role: isUserRole(rowData.role) ? rowData.role : UserRole.STUDENT,
      photo: undefined,
      dob: rowData.dob,
      city: rowData.city,
      coursesOptions: cOpts,
    };
    setDefaultFormValues(editableData);
    setModalIsOpen(true);
    return true;
  };
  const handleUserDelete = (rowData: UserData) => {
    setPendingDeleteUser(rowData);
    if (rowData.email === currentUser.email) {
      setPendingDeleteUser(null);
      setConfirmation(6);
      setShowConfirm(true);
      return;
    }
    setConfirmation(4);
    setShowConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;

    try {
      await deleteUser(pendingDeleteUser).unwrap();
      setConfirmation(3); // 3 = deletion success message
    } catch (error) {
      console.error('Delete error:', error);
      setConfirmation(2); // 2 = error
    } finally {
      setPendingDeleteUser(null);
      setShowConfirm(true);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setFile(e.target.files?.[0] ?? null);
  };

  const handleImportUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Uploading file:', file);
    if (!file) return;

    setLoading(true);
    try {
      const f = await file.arrayBuffer();
      const wb = read(f); // parse the array buffer
      const ws = wb.Sheets[wb.SheetNames[0]]; // get the first worksheet
      const rows = utils.sheet_to_json(ws, {
        header: 1,
      }); // generate objects
      const columns = rows[0] as string[];
      console.log('Rows from file:', rows);
      console.log('Columns found:', columns);
      if (
        !columns.includes('firstName') ||
        !columns.includes('lastName') ||
        !columns.includes('email') ||
        !columns.includes('telegram') ||
        !columns.includes('dob') ||
        !columns.includes('city')
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid file format',
          text: 'The uploaded file is missing one or more required columns.',
        });
        return;
      }

      const fd = new FormData();
      fd.append('file', file);
      const res = await importUsers(fd);
      console.log('res', res);
      setResult(res);
      setImportResultModalOpen(true);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setResult({
        success: false,
        imported: 0,
        existingUsers: 0,
        errors: [{ row: 0, messages: ['Upload failed'] }],
        credentials: [],
      });
    } finally {
      setLoading(false);
      setOpenFileUpload(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
    setOpenFileUpload(false);
  };

  const downloadCsv = (key: 'credentials' | 'errors') => {
    if (key === 'credentials' && !result?.credentials?.length) return;
    if (key === 'errors' && !result?.errors?.length) return;
    let csv = '';
    if (key === 'errors') {
      csv =
        'row,messages,email,firstName,lastName\n' +
        result?.errors
          .map(
            (e) =>
              `${e.row},"${e.messages.join('; ')}",${e.rowData?.email},${e.rowData?.firstName},${e.rowData?.lastName}`,
          )
          .join('\n');
    } else {
      csv =
        'email,password\n' + result?.credentials.map((c) => `${c.email},${c.password}`).join('\n');
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = key === 'credentials' ? 'credentials.csv' : 'errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  useEffect(() => {
    console.log('coursesListOptions', coursesListOptions);
  }, [coursesListOptions]);
  return (
    <>
      <Breadcrumbs mainTitle={t('users.usersList.usersListPageTitle')} />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              {/* <CommonCardHeader title={StudentsListCardTitle} tag /> */}
              <CardBody>
                <Row className="mb-3">
                  <Col>
                    {' '}
                    <p>{t('users.usersList.usersListPageParagraph')}</p>
                  </Col>
                  <Col className="d-flex justify-content-end ">
                    <div className="d-flex pr-4">
                      <Button
                        id={AddStudentButtonProps.id}
                        // className={`${className ? className : ""}${
                        //   raised ? ` btn-air-${data.color}` : ""
                        // }`}
                        outline={AddStudentButtonProps.outline}
                        disabled={buttonDisabled}
                        color={AddStudentButtonProps.color}
                        onClick={addNewUserButtonHandler}
                        className="me-4"
                      >
                        {t('users.usersList.createUserBtn')}
                      </Button>
                      <Button onClick={() => setOpenFileUpload(true)}>
                        {t('users.usersList.importUsers')}
                      </Button>
                    </div>
                  </Col>
                </Row>

                <AjaxSourcedData
                  data={data}
                  columns={getUsersListTableColumns(
                    handleUserEdit,
                    handleUserDelete,
                    t,
                    role,
                    pageLang,
                  )}
                  noDataComponent={<NoDataComponent />}
                  loading={isLoading || isFetching}
                  // title="List of users"
                  // subTitle={
                  //   Array.isArray(StudentsListTableSubtitle)
                  //     ? StudentsListTableSubtitle.map((sub) => sub.text).join(', ')
                  //     : StudentsListTableSubtitle
                  // }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <SimpleModal isOpen={modalIsOpen} close={() => setModalIsOpen(false)} size="xl">
        {/* {modalContent} */}
        <AddUserForm
          // inputs={userAddInputs}
          inputs={getUserAddInputs(t, modalAction.id, coursesListOptions)}
          formTitle={
            modalAction.id === 1
              ? t('users.usersList.createUserFormTitle')
              : t('users.usersList.editUserFormTitle')
          }
          formSubTitle={t('users.usersList.createUserFormSubtitle')}
          submitHandler={modalAction.id === 1 ? newUserSubmitHandler : editUserSubmitHandler}
          defaultValues={defaultFormValues}
          mode={modalAction.id === 1 ? 'create' : 'edit'}
          coursesList={coursesListOptions}
          subject="user"
        />
      </SimpleModal>
      <SimpleModal isOpen={showConfirm} close={() => setShowConfirm(false)}>
        <Confirmation
          keyValue={confirmation}
          applyHandler={confirmDeleteUser}
          cancelHandler={() => {
            setShowConfirm(false);
            setPendingDeleteUser(null);
            setConfirmation(null);
          }}
          close={() => setShowConfirm(false)}
          email={pendingDeleteUser?.email}
        />
      </SimpleModal>
      <SimpleModal isOpen={openFileUpload} close={() => setOpenFileUpload(false)}>
        <Card>
          <CardHeader>
            <CommonCardHeader title={t('users.usersList.importUsers')} />
          </CardHeader>
          <CardBody>
            <CardText>
              <h5>{t('users.usersList.importInstructs')}</h5>
              <div className="d-flex flex-column gap-2 mt-3">
                <p className="p-0 m-0">{t('users.usersList.importFirstNameColumn')}</p>
                <p className="p-0 m-0">{t('users.usersList.importLastNameColumn')}</p>
                <p className="p-0 m-0">{t('users.usersList.importEmailComlumn')}</p>
                <p className="p-0 m-0">{t('users.usersList.importTelegramColumn')}</p>
                <p className="p-0 m-0">{t('users.usersList.importDOBColumn')}</p>
                <p className="p-0 m-0">{t('users.usersList.importCityColumn')}</p>
              </div>
            </CardText>
            <Form onSubmit={handleImportUpload} encType="multipart/form-data">
              <Input
                innerRef={fileRef}
                type="file"
                onChange={handleFileChange}
                accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              />
              <div className="mt-2 d-flex gap-2 mt-3">
                <Button type="submit" color="primary" disabled={!file || loading}>
                  {loading ? t('global.uploading') : 'Ok'}
                </Button>
                <Button type="button" color="secondary" onClick={handleCancel}>
                  {t('global.cancel')}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </SimpleModal>
      <SimpleModal isOpen={importResultModalOpen} close={() => setImportResultModalOpen(false)}>
        {result && (
          <div className="mt-3">
            {result.credentials.length === 0 && (
              <>
                <h2>{t('users.import.noAddedOnImport')}</h2>
              </>
            )}
            {result.credentials?.length ? (
              <Card>
                <CardBody>
                  <h2 className="mb-2">{t('users.import.downloadCreds')}</h2>
                  <Button color="success" onClick={() => downloadCsv('credentials')}>
                    {t('users.import.downloadCSV')}
                  </Button>
                </CardBody>
              </Card>
            ) : null}

            {result.errors?.length ? (
              <details className="mt-2">
                <summary className="mb-2">{t('users.import.showRowErrors')}</summary>
                <Button color="warning" onClick={() => downloadCsv('errors')}>
                  {t('users.import.downloadErrors')}
                </Button>
                {/* <ul className="mt-2">
                  {result.errors.map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.messages.join('; ')}
                    </li>
                  ))}
                </ul> */}
              </details>
            ) : null}
          </div>
        )}
      </SimpleModal>
    </>
  );
};

export default UsersListPageContainer;
